// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — AdminProcesosPage (Prompt 153: fix + grid)
// CRUD de procesos y grupos funcionales — grid responsive
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, type JSX } from 'react';
import { fetchProcesos, request } from '../../../shared/services/api';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import type { Proceso, TipologiaProceso } from '../../../../types';
import { TIPOLOGIA_LABEL } from '../../../../types';

// ─── Styles ──────────────────────────────────────────────────────────────────

const C = {
  navy: '#0E243E',
  azul1: '#1F3864',
  azul2: '#2F5496',
  celeste: '#33BCE1',
  text: '#1E293B',
  textSec: '#64748B',
  textMut: '#94A3B8',
  border: '#d5d5d5',
  bg: '#F7F8FA',
  surface: '#fff',
};

const CARD_STYLE: React.CSSProperties = {
  background: C.surface,
  borderRadius: 12,
  border: `1px solid ${C.border}`,
  borderTop: `3px solid ${C.navy}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'hidden',
};

const HEADER_STYLE: React.CSSProperties = {
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderBottom: '1px solid #E8E8E8',
  background: '#FAFBFC',
  fontSize: 18,
  fontWeight: 700,
  color: C.navy,
  cursor: 'pointer',
};

// ─── Tipologia SVGs ──────────────────────────────────────────────────────────

const TIPO_ICON: Record<string, JSX.Element> = {
  PE: (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.azul1}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
    </svg>
  ),
  PO: (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.celeste}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="5" cy="6" r="3" />
      <circle cx="19" cy="6" r="3" />
      <circle cx="12" cy="18" r="3" />
      <path d="M8 6h8M5 9v3l7 3M19 9v3l-7 3" />
    </svg>
  ),
  PS: (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#12B76A"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface GroupedData {
  tipologia: TipologiaProceso;
  groups: { grupoNumero: number; grupoNombre: string; procesos: Proceso[] }[];
}

function groupProcesos(procesos: Proceso[]): GroupedData[] {
  return (['PE', 'PO', 'PS'] as TipologiaProceso[]).map((tip) => {
    const tipProcs = procesos.filter((p) => p.tipologia === tip);
    const groupMap: Record<number, { nombre: string; procs: Proceso[] }> = {};
    tipProcs.forEach((p) => {
      if (!groupMap[p.grupoNumero]) groupMap[p.grupoNumero] = { nombre: p.grupoNombre, procs: [] };
      groupMap[p.grupoNumero].procs.push(p);
    });
    return {
      tipologia: tip,
      groups: Object.entries(groupMap)
        .map(([num, g]) => ({
          grupoNumero: parseInt(num, 10),
          grupoNombre: g.nombre,
          procesos: g.procs.sort((a, b) => a.codigo.localeCompare(b.codigo)),
        }))
        .sort((a, b) => a.grupoNumero - b.grupoNumero),
    };
  });
}

// Chevron component
function Chev({ open, size = 10 }: { open: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.textMut}
      strokeWidth="2.5"
      style={{
        transform: open ? 'rotate(0)' : 'rotate(-90deg)',
        transition: 'transform 0.2s',
        flexShrink: 0,
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AdminProcesosPage() {
  const { t, lang } = useI18n();
  const ap = t.adminProcesos;
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    PE: true,
    PO: true,
    PS: true,
  });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState('');

  // Inline edit states
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editGroupValue, setEditGroupValue] = useState('');
  const [editingProc, setEditingProc] = useState<string | null>(null);
  const [editProcValue, setEditProcValue] = useState('');
  const [newProcGroup, setNewProcGroup] = useState<string | null>(null);
  const [newProcValue, setNewProcValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Proceso | null>(null);

  // Pending groups (created in UI, not yet in BD)
  const [pendingGroups, setPendingGroups] = useState<
    { tipologia: string; nombre: string; numero: number }[]
  >([]);
  const [newGroupTip, setNewGroupTip] = useState<string | null>(null);
  const [newGroupValue, setNewGroupValue] = useState('');

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }, []);

  const reload = useCallback(() => {
    fetchProcesos()
      .then((r) => setProcesos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload, lang]);

  const grouped = groupProcesos(procesos);
  const totalGroups = grouped.reduce((s, g) => s + g.groups.length, 0);

  // ─── CRUD handlers ───

  const handleRenameGroup = async (tipologia: string, grupoNumero: number, nuevoNombre: string) => {
    if (!nuevoNombre.trim()) return;
    try {
      await request('/procesos/grupo', {
        method: 'PATCH',
        body: JSON.stringify({ tipologia, grupoNumero, nuevoNombre: nuevoNombre.trim() }),
      });
      showToast(`${ap.grupoRenombrado}: ${nuevoNombre.trim()}`);
      reload();
    } catch (err: any) {
      showToast(`Error: ${err.message}`);
    }
    setEditingGroup(null);
  };

  const handleCreateProc = async (
    tipologia: string,
    grupoNumero: number,
    grupoNombre: string,
    nombre: string,
  ) => {
    if (!nombre.trim()) return;
    try {
      const existing = procesos.filter(
        (p) => p.tipologia === tipologia && p.grupoNumero === grupoNumero,
      );
      const maxOrden = existing.reduce((m, p) => {
        const num = parseInt(p.codigo.split('.')[1] ?? '0', 10);
        return num > m ? num : m;
      }, grupoNumero * 10);
      const codigo = `${tipologia}.${String(maxOrden + 1).padStart(2, '0')}`;

      await request('/procesos', {
        method: 'POST',
        body: JSON.stringify({
          codigo,
          nombre: nombre.trim(),
          tipologia,
          grupoNumero,
          grupoNombre,
        }),
      });
      showToast(`${ap.procesoCreado}: ${codigo}`);
      // Remove pending group if exists
      setPendingGroups((pg) =>
        pg.filter((g) => !(g.tipologia === tipologia && g.numero === grupoNumero)),
      );
      reload();
    } catch (err: any) {
      showToast(`Error: ${err.message}`);
    }
    setNewProcGroup(null);
    setNewProcValue('');
  };

  const handleEditProc = async (id: string, nombre: string) => {
    if (!nombre.trim()) return;
    try {
      await request(`/procesos/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ nombre: nombre.trim() }),
      });
      showToast(ap.procesoActualizado);
      reload();
    } catch (err: any) {
      showToast(`Error: ${err.message}`);
    }
    setEditingProc(null);
  };

  const handleDeactivate = async (proc: Proceso) => {
    try {
      await request(`/procesos/${proc.id}`, { method: 'DELETE' });
      showToast(`${ap.procesoDesactivado}: ${proc.codigo}`);
      reload();
    } catch (err: any) {
      showToast(`Error: ${err.message}`);
    }
    setConfirmDelete(null);
  };

  const handleCreateGroup = (tipologia: string, nombre: string) => {
    if (!nombre.trim()) {
      setNewGroupTip(null);
      return;
    }
    const existingNums =
      grouped.find((g) => g.tipologia === tipologia)?.groups.map((g) => g.grupoNumero) ?? [];
    const pendingNums = pendingGroups.filter((g) => g.tipologia === tipologia).map((g) => g.numero);
    const maxNum = Math.max(0, ...existingNums, ...pendingNums);
    setPendingGroups((pg) => [...pg, { tipologia, nombre: nombre.trim(), numero: maxNum + 1 }]);
    setNewGroupTip(null);
    setNewGroupValue('');
  };

  const handleDeleteGroup = (tipologia: string, grupoNumero: number, procCount: number) => {
    if (procCount > 0) {
      showToast(ap.grupoConProcesos);
      return;
    }
    // Remove pending group
    setPendingGroups((pg) =>
      pg.filter((g) => !(g.tipologia === tipologia && g.numero === grupoNumero)),
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <LoadingSpinner text={ap.cargando} />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 20px', overflowY: 'auto', height: '100%', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy, margin: 0 }}>{ap.titulo}</h2>
        <p style={{ fontSize: 12, color: C.textMut, margin: '4px 0 0' }}>
          {totalGroups} {ap.grupos} · {procesos.length} {ap.procesos}
        </p>
      </div>

      {/* Grid responsive — 3 cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(627px, 1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {grouped.map(({ tipologia, groups }) => {
          const isExp = expanded[tipologia] ?? true;
          const tipPending = pendingGroups.filter((g) => g.tipologia === tipologia);

          return (
            <div key={tipologia} style={CARD_STYLE}>
              <div
                style={HEADER_STYLE}
                onClick={() => setExpanded((s) => ({ ...s, [tipologia]: !isExp }))}
              >
                {TIPO_ICON[tipologia]}
                <span style={{ flex: 1 }}>{TIPOLOGIA_LABEL[tipologia]}</span>
                <span style={{ fontSize: 13, color: C.textMut, fontWeight: 500 }}>
                  {groups.length + tipPending.length} ·{' '}
                  {groups.reduce((s, g) => s + g.procesos.length, 0)}
                </span>
                <Chev open={isExp} size={12} />
              </div>

              {isExp && (
                <div style={{ padding: '6px 10px 10px' }}>
                  {groups.map((group) => {
                    const gKey = `${tipologia}-${group.grupoNumero}`;
                    const isGExp = expandedGroups[gKey] ?? true;
                    const isEditingG = editingGroup === gKey;

                    return (
                      <div key={gKey} style={{ marginBottom: 4 }}>
                        {/* Group header */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '5px 2px',
                            cursor: 'pointer',
                            borderRadius: 6,
                          }}
                          onClick={() => {
                            if (!isEditingG) setExpandedGroups((s) => ({ ...s, [gKey]: !isGExp }));
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F8F9FB';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <Chev open={isGExp} />
                          {isEditingG ? (
                            <input
                              autoFocus
                              value={editGroupValue}
                              onChange={(e) => setEditGroupValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  handleRenameGroup(tipologia, group.grupoNumero, editGroupValue);
                                if (e.key === 'Escape') setEditingGroup(null);
                              }}
                              onBlur={() => setEditingGroup(null)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.azul2,
                                border: `1px solid ${C.celeste}`,
                                borderRadius: 4,
                                padding: '1px 5px',
                                fontFamily: 'inherit',
                                flex: 1,
                              }}
                            />
                          ) : (
                            <span
                              style={{ fontSize: 14, fontWeight: 600, color: C.azul2, flex: 1 }}
                            >
                              Gr.{String(group.grupoNumero).padStart(2, '0')} {group.grupoNombre}
                            </span>
                          )}
                          <span style={{ fontSize: 9, color: C.textMut }}>
                            {group.procesos.length}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingGroup(gKey);
                              setEditGroupValue(group.grupoNombre);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 1,
                              fontSize: 10,
                              opacity: 0.4,
                            }}
                            title={ap.renombrar}
                          >
                            ✎
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteGroup(
                                tipologia,
                                group.grupoNumero,
                                group.procesos.length,
                              );
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 1,
                              fontSize: 10,
                              opacity: 0.4,
                              color: '#DC2626',
                            }}
                            title={ap.eliminarGrupo}
                          >
                            🗑
                          </button>
                        </div>

                        {/* Processes */}
                        {isGExp && (
                          <div style={{ paddingLeft: 16 }}>
                            {group.procesos.map((proc) => (
                              <ProcRow
                                key={proc.id}
                                proc={proc}
                                isEditing={editingProc === proc.id}
                                editValue={editProcValue}
                                onStartEdit={() => {
                                  setEditingProc(proc.id);
                                  setEditProcValue(proc.nombre);
                                }}
                                onChangeEdit={setEditProcValue}
                                onConfirmEdit={() => handleEditProc(proc.id, editProcValue)}
                                onCancelEdit={() => setEditingProc(null)}
                                onDelete={() => setConfirmDelete(proc)}
                                ap={ap}
                              />
                            ))}
                            {newProcGroup === gKey ? (
                              <div style={{ padding: '3px 6px' }}>
                                <input
                                  autoFocus
                                  value={newProcValue}
                                  onChange={(e) => setNewProcValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter')
                                      handleCreateProc(
                                        tipologia,
                                        group.grupoNumero,
                                        group.grupoNombre,
                                        newProcValue,
                                      );
                                    if (e.key === 'Escape') {
                                      setNewProcGroup(null);
                                      setNewProcValue('');
                                    }
                                  }}
                                  onBlur={() => {
                                    setNewProcGroup(null);
                                    setNewProcValue('');
                                  }}
                                  placeholder={ap.nombreProceso}
                                  style={{
                                    fontSize: 11,
                                    width: '100%',
                                    border: `1px solid ${C.celeste}`,
                                    borderRadius: 4,
                                    padding: '3px 6px',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box',
                                  }}
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setNewProcGroup(gKey);
                                  setNewProcValue('');
                                }}
                                style={{
                                  fontSize: 13,
                                  color: C.textMut,
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '3px 6px',
                                  fontFamily: 'inherit',
                                }}
                              >
                                {ap.nuevoProceso}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Pending groups (not yet in BD) */}
                  {tipPending.map((pg) => {
                    const pgKey = `${tipologia}-${pg.numero}`;
                    return (
                      <div key={pgKey} style={{ marginBottom: 4 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '5px 2px',
                          }}
                        >
                          <Chev open />
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: C.azul2,
                              flex: 1,
                              fontStyle: 'italic',
                            }}
                          >
                            Gr.{String(pg.numero).padStart(2, '0')} {pg.nombre}
                          </span>
                          <span style={{ fontSize: 9, color: C.textMut }}>0</span>
                          <button
                            onClick={() => handleDeleteGroup(tipologia, pg.numero, 0)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 1,
                              fontSize: 10,
                              opacity: 0.4,
                              color: '#DC2626',
                            }}
                          >
                            🗑
                          </button>
                        </div>
                        <div style={{ paddingLeft: 16 }}>
                          {newProcGroup === pgKey ? (
                            <input
                              autoFocus
                              value={newProcValue}
                              onChange={(e) => setNewProcValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  handleCreateProc(tipologia, pg.numero, pg.nombre, newProcValue);
                                if (e.key === 'Escape') {
                                  setNewProcGroup(null);
                                  setNewProcValue('');
                                }
                              }}
                              onBlur={() => {
                                setNewProcGroup(null);
                                setNewProcValue('');
                              }}
                              placeholder={ap.nombreProceso}
                              style={{
                                fontSize: 11,
                                width: '100%',
                                border: `1px solid ${C.celeste}`,
                                borderRadius: 4,
                                padding: '3px 6px',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                              }}
                            />
                          ) : (
                            <button
                              onClick={() => {
                                setNewProcGroup(pgKey);
                                setNewProcValue('');
                              }}
                              style={{
                                fontSize: 10,
                                color: C.textMut,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '3px 6px',
                                fontFamily: 'inherit',
                              }}
                            >
                              {ap.nuevoProceso}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* + Nuevo grupo */}
                  {newGroupTip === tipologia ? (
                    <div style={{ padding: '4px 2px' }}>
                      <input
                        autoFocus
                        value={newGroupValue}
                        onChange={(e) => setNewGroupValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreateGroup(tipologia, newGroupValue);
                          if (e.key === 'Escape') {
                            setNewGroupTip(null);
                            setNewGroupValue('');
                          }
                        }}
                        onBlur={() => {
                          setNewGroupTip(null);
                          setNewGroupValue('');
                        }}
                        placeholder={ap.nombreGrupo}
                        style={{
                          fontSize: 11,
                          width: '100%',
                          border: `1px solid ${C.celeste}`,
                          borderRadius: 4,
                          padding: '3px 6px',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setNewGroupTip(tipologia);
                        setNewGroupValue('');
                      }}
                      style={{
                        fontSize: 13,
                        color: C.textMut,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 2px',
                        fontFamily: 'inherit',
                      }}
                    >
                      {ap.nuevoGrupo}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <>
          <div
            onClick={() => setConfirmDelete(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,45,0.32)', zIndex: 200 }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              width: 'min(400px, 90vw)',
              background: C.surface,
              borderRadius: 12,
              borderTop: `3px solid ${C.navy}`,
              boxShadow: '0 16px 48px rgba(15,25,60,0.22)',
              padding: 24,
              zIndex: 201,
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: '0 0 8px' }}>
              {ap.confirmarDesactivar}
            </h3>
            <p style={{ fontSize: 13, color: C.textSec, margin: '0 0 12px' }}>
              <strong>{confirmDelete.codigo}</strong> "{confirmDelete.nombre}"{' '}
              {ap.confirmarDesactivarDesc}
            </p>
            {(confirmDelete._count?.documentos ?? 0) > 0 && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: '#FFF7ED',
                  border: '1px solid #FED7AA',
                  marginBottom: 12,
                  fontSize: 12,
                  color: '#9A3412',
                }}
              >
                {confirmDelete._count!.documentos} {ap.avisoDocumentos}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  padding: '6px 16px',
                  fontSize: 12,
                  fontWeight: 500,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  background: C.surface,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {ap.cancelar}
              </button>
              <button
                onClick={() => handleDeactivate(confirmDelete)}
                style={{
                  padding: '6px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 8,
                  background: '#DC2626',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {ap.desactivar}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 300,
            padding: '10px 20px',
            borderRadius: 8,
            background: C.navy,
            color: '#fff',
            fontSize: 12,
            fontWeight: 500,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

// ─── ProcRow (compact) ───────────────────────────────────────────────────────

function ProcRow({
  proc,
  isEditing,
  editValue,
  onStartEdit,
  onChangeEdit,
  onConfirmEdit,
  onCancelEdit,
  onDelete,
  ap,
}: {
  proc: Proceso;
  isEditing: boolean;
  editValue: string;
  onStartEdit: () => void;
  onChangeEdit: (v: string) => void;
  onConfirmEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  ap: any;
}) {
  const inactive = !proc.activo;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 6px',
        borderRadius: 6,
        background: inactive ? 'rgba(220,38,38,0.04)' : 'transparent',
        opacity: inactive ? 0.5 : 1,
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!inactive) e.currentTarget.style.background = '#F8F9FB';
        e.currentTarget.querySelectorAll<HTMLElement>('.act-btn').forEach((b) => {
          b.style.opacity = '1';
        });
      }}
      onMouseLeave={(e) => {
        if (!inactive) e.currentTarget.style.background = 'transparent';
        e.currentTarget.querySelectorAll<HTMLElement>('.act-btn').forEach((b) => {
          b.style.opacity = '0';
        });
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          fontWeight: 600,
          color: C.azul2,
          minWidth: 46,
          flexShrink: 0,
        }}
      >
        {proc.codigo}
      </span>
      {isEditing ? (
        <input
          autoFocus
          value={editValue}
          onChange={(e) => onChangeEdit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirmEdit();
            if (e.key === 'Escape') onCancelEdit();
          }}
          onBlur={onCancelEdit}
          style={{
            fontSize: 14,
            flex: 1,
            border: `1px solid ${C.celeste}`,
            borderRadius: 4,
            padding: '1px 5px',
            fontFamily: 'inherit',
          }}
        />
      ) : (
        <span
          style={{
            fontSize: 14,
            color: C.text,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: inactive ? 'line-through' : 'none',
          }}
        >
          {proc.nombre}
        </span>
      )}
      {proc.direccionResponsable && (
        <span
          style={{
            fontSize: 12,
            color: C.textMut,
            whiteSpace: 'nowrap',
            maxWidth: 130,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {proc.direccionResponsable.puesto.titulo}
        </span>
      )}
      {(proc._count?.documentos ?? 0) > 0 && (
        <span
          style={{
            fontSize: 12,
            padding: '0 6px',
            borderRadius: 10,
            fontWeight: 700,
            background: 'rgba(51,188,225,0.10)',
            color: C.celeste,
            flexShrink: 0,
          }}
        >
          {proc._count!.documentos}
        </span>
      )}
      {inactive && (
        <span
          style={{
            fontSize: 11,
            padding: '0 5px',
            borderRadius: 4,
            background: '#FEE2E2',
            color: '#DC2626',
            fontWeight: 600,
          }}
        >
          {ap.inactivo}
        </span>
      )}
      {!inactive && (
        <>
          <button
            className="act-btn"
            onClick={onStartEdit}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 1,
              fontSize: 10,
              opacity: 0,
            }}
          >
            ✎
          </button>
          <button
            className="act-btn"
            onClick={onDelete}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 1,
              fontSize: 10,
              opacity: 0,
              color: '#DC2626',
            }}
          >
            🗑
          </button>
        </>
      )}
    </div>
  );
}
